// Kept import-free (like push-error-classifier.ts) so it stays unit-testable
// in vitest's node environment — push-notifications.tsx transitively imports
// react-native, which vitest can't parse.

/**
 * Error fields for Sentry that survive server-side data scrubbing. The
 * scrubber redacts extras keyed `error` / `errorMessage` to "[Filtered]" —
 * which is exactly what blinded us on TASKHEALTH-MOBILE-2C/2E (~500 events,
 * ~50 nurses, underlying cause invisible). `errorName` demonstrably survives,
 * so report the message under a same-family key the scrub list doesn't match.
 */
export function describePushError(err: unknown): { errorName: string; errorSummary: string } {
    const name = err instanceof Error ? err.name : typeof err;
    const summary = String(err instanceof Error ? err.message : err).slice(0, 200);
    return { errorName: name, errorSummary: `${name}: ${summary}` };
}

/**
 * getExpoPushTokenAsync talks to Expo's token service (and APNs/FCM under the
 * hood) and fails transiently — a single failed fetch at app start was the
 * dominant source of token_or_register_failed / token_refresh_failed events
 * (TASKHEALTH-MOBILE-2C/2E). Retry with short backoff before surfacing a real
 * failure. `fetchToken` is injected so this stays free of expo imports.
 */
export async function getPushTokenWithRetry(
    fetchToken: () => Promise<string>,
    onRetry?: (attempt: number, err: unknown) => void,
    delaysMs: number[] = [1_000, 3_000]
): Promise<string> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await fetchToken();
        } catch (err) {
            if (attempt >= delaysMs.length) {
                throw err;
            }
            onRetry?.(attempt + 1, err);
            await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
        }
    }
}
